import { Link } from "react-router-dom";
import { ArrowRight, Hammer, ShoppingBag, Users, Sparkles, Home, Compass, Coins, Clock, MapPin, PiggyBank, Truck, HandCoins, CalendarDays, Video, Globe, Gift, Mic, CalendarCheck, Mail, Heart } from "lucide-react";
import { IMAGES, HOME_SERVICES, HOME_SECONDARY } from "@/data/site";
import { Reveal } from "@/components/Reveal";

const ICONS = { Hammer, ShoppingBag, Users, Sparkles, Home, Compass, Coins, Clock, MapPin, PiggyBank, Truck, HandCoins, CalendarDays, Video, Globe, Gift, Mic, CalendarCheck, Mail, Heart };
const Icon = ({ name, className }) => { const C = ICONS[name] || Compass; return <C className={className} />; };

const Formules = ({ formules }) => (
  <ul className="mt-7 grid sm:grid-cols-2 gap-3" data-testid="service-formules">
    {formules.map((f) => (
      <li key={f.id}>
        <Link to={f.path} data-testid={`service-formule-${f.id}`} className="group/f block h-full rounded-xl border border-sand-dark bg-card px-4 py-3.5 hover:border-terra transition-colors duration-300">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-bark leading-snug">{f.titre}</span>
            <span className="font-mono text-xs text-terra shrink-0">{f.prix}</span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-fumee mt-1">{f.format}</p>
          <p className="text-xs text-fumee leading-relaxed mt-1.5">{f.texte}</p>
        </Link>
      </li>
    ))}
  </ul>
);

const Specs = ({ specs, light = false, compact = false }) => specs?.length ? (
  <dl className={`mt-7 divide-y ${light ? "divide-sand/15" : "divide-sand-dark"}`}>
    {specs.map((s) => (
      <div key={s.label} className="py-3 flex gap-4 items-start">
        <Icon name={s.icon} className={`w-4 h-4 mt-0.5 shrink-0 ${light ? "text-terra" : "text-terra"}`} />
        <div className={`flex flex-col min-w-0 ${compact ? "" : "sm:flex-row sm:gap-4 sm:items-baseline"}`}>
          <dt className={`font-mono text-[10px] uppercase tracking-[0.2em] shrink-0 ${compact ? "mb-0.5" : "sm:w-36"} ${light ? "text-sand/55" : "text-fumee"}`}>{s.label}</dt>
          <dd className={`text-sm leading-relaxed ${light ? "text-sand/90" : "text-bark"}`}>{s.valeur}</dd>
        </div>
      </div>
    ))}
  </dl>
) : null;

const Tags = ({ tags, light = false }) => (
  <div className="mt-5 flex flex-wrap gap-2">
    {tags.map((t) => (
      <span key={t} className={`px-3 py-1.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.15em] ${light ? "border-sand/25 text-sand/70" : "border-wood/20 text-fumee"}`}>{t}</span>
    ))}
  </div>
);

const Picture = ({ service, products }) => {
  if (service.produits && products?.length >= 3) {
    return (
      <div className="grid grid-cols-3 gap-3 h-full" data-testid="service-picture-boutique">
        {products.slice(0, 3).map((p, i) => (
          <div key={p.id} className={`rounded-2xl overflow-hidden bg-sand-dark ${i === 1 ? "translate-y-6" : ""}`}>
            <img src={p.image} alt={p.nom} loading="lazy" className="w-full h-full object-cover aspect-[3/4] transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={`relative rounded-2xl overflow-hidden ${service.featured ? "aspect-[4/3] lg:aspect-[5/4]" : "aspect-[4/3]"}`}>
      <img src={IMAGES[service.image]} alt={service.titre} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
      <span className="absolute top-4 left-4 font-serif text-5xl text-sand/90 drop-shadow-md">{service.rang}</span>
    </div>
  );
};

const Volet = ({ v, products }) => (
  <div className="bg-card border border-sand-dark rounded-2xl p-6 flex flex-col" data-testid={`service-volet-${v.id}`}>
    <h4 className="font-serif text-xl text-bark">{v.titre}</h4>
    {v.produits && products?.length >= 3 && (
      <div className="grid grid-cols-3 gap-2 mt-4" data-testid="service-picture-boutique">
        {products.slice(0, 3).map((p) => (
          <Link key={p.id} to={`/boutique/tambour/${p.id}`} data-testid={`service-product-${p.id}`} className="rounded-xl overflow-hidden bg-sand-dark aspect-square block">
            <img src={p.image} alt={p.nom} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
          </Link>
        ))}
      </div>
    )}
    <Specs specs={v.specs} compact />
    <Tags tags={v.tags} />
    <div className="mt-6 pt-2 flex flex-wrap items-center gap-4">
      <Link to={v.path} data-testid={`service-cta-${v.id}`}
        className="group/btn inline-flex items-center gap-3 px-6 py-3 rounded-full bg-earth text-sand text-sm tracking-wide hover:bg-terra hover:text-earth transition-colors duration-500">
        {v.cta} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
      </Link>
      {v.secondaire && <Link to={v.secondaire.path} data-testid={`service-secondary-${v.id}`} className="link-line text-sm text-fumee hover:text-bark">{v.secondaire.label}</Link>}
    </div>
  </div>
);

export const HomeServices = ({ products, extras = {}, ids = null }) => (
  <div className="space-y-24 lg:space-y-32" data-testid="home-services">
    {HOME_SERVICES.filter((s) => !ids || ids.includes(s.id)).map((s, i) => {
      const reverse = i % 2 === 1;
      if (s.volets) {
        return (
          <Reveal key={s.id}>
            <article className="group grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-testid={`service-card-${s.id}`}>
              <Link to={s.path} aria-label={s.titre} className="block lg:col-span-5 lg:sticky lg:top-28">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
                  <img src={IMAGES[s.image]} alt={s.titre} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
                  <span className="absolute top-4 left-4 font-serif text-5xl text-sand/90 drop-shadow-md">{s.rang}</span>
                </div>
              </Link>
              <div className="lg:col-span-7">
                <p className="eyebrow flex items-center gap-3"><Icon name={s.icon} className="w-4 h-4 text-terra" /> {s.rang} — {s.court}</p>
                <h3 className="font-serif text-bark mt-5 leading-tight text-3xl sm:text-4xl lg:text-5xl">{s.titre}</h3>
                <p className="text-fumee mt-5 leading-relaxed max-w-2xl">{s.tagline}</p>
                <div className="grid md:grid-cols-2 gap-5 mt-10">
                  {s.volets.map((v) => <Volet key={v.id} v={v} products={products} />)}
                </div>
              </div>
            </article>
          </Reveal>
        );
      }
      return (
        <Reveal key={s.id}>
          <article className="group grid lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-testid={`service-card-${s.id}`}>
            <Link to={s.path} aria-label={s.titre} className={`block lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
              <Picture service={s} products={products} />
            </Link>
            <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
              <p className="eyebrow flex items-center gap-3"><Icon name={s.icon} className="w-4 h-4 text-terra" /> {s.rang} — {s.court}</p>
              <h3 className={`font-serif text-bark mt-5 leading-tight ${s.featured ? "text-3xl sm:text-4xl lg:text-5xl" : "text-3xl lg:text-4xl"}`}>{s.titre}</h3>
              <p className="text-fumee mt-5 leading-relaxed max-w-xl">{s.tagline}</p>
              {s.formules ? <Formules formules={s.formules} /> : <Specs specs={s.specs} />}
              <Tags tags={s.tags} />
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to={s.path} data-testid={`service-cta-${s.id}`}
                  className="group/btn inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-earth text-sand text-sm tracking-wide hover:bg-terra hover:text-earth transition-colors duration-500">
                  {s.cta} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                </Link>
                {s.secondaire && (
                  <Link to={s.secondaire.path} data-testid={`service-secondary-${s.id}`} className="link-line text-sm text-fumee hover:text-bark">{s.secondaire.label}</Link>
                )}
              </div>
            </div>
          </article>
          {extras[s.id]}
        </Reveal>
      );
    })}
  </div>
);

export const HomeSecondary = ({ ids = null }) => (
  <div className={`grid md:grid-cols-2 gap-6 ${!ids || ids.length > 2 ? "lg:grid-cols-4" : ""}`} data-testid="home-secondary">
    {HOME_SECONDARY.filter((s) => !ids || ids.includes(s.id)).map((s, i) => (
      <Reveal key={s.id} delay={i * 0.1}>
        <Link to={s.path} data-testid={`secondary-card-${s.id}`} className="group relative block rounded-2xl overflow-hidden min-h-[22rem] bg-earth">
          <img src={IMAGES[s.image]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-50 transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/60 to-earth/10" />
          <div className="relative z-[2] h-full flex flex-col justify-end p-8 text-sand">
            <Icon name={s.icon} className="w-5 h-5 text-terra mb-4" />
            <h3 className="font-serif text-2xl lg:text-3xl leading-tight">{s.titre}</h3>
            <p className="text-sm text-sand/70 mt-3 leading-relaxed">{s.texte}</p>
            <span className="inline-flex items-center gap-2 mt-6 text-sm link-line">{s.cta} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
          </div>
        </Link>
      </Reveal>
    ))}
  </div>
);
